// frontend/src/components/JournalSection/JournalSection.js
import React, { useState, useRef, useEffect } from 'react';
import apiClient from '../../apiClient';
import { useQuickPhrases } from '../../hooks/useQuickPhrases';
import QuickPhraseDropdown from '../QuickPhraseDropdown';
import './JournalSection.css';

// Near-realtime streaming settings
const CHUNK_START_AFTER = 1.5;  // Starta efter 1.5 sek (behöver lite audio)
const CHUNK_INTERVAL = 2.0;     // Skicka chunk var 2:a sekund (balans mellan latency och stabilitet)

// Bestäm bästa mimeType för inspelning (prioritera OPUS för bättre komprimering)
const getSupportedMimeType = () => {
  const mimeTypes = [
    'audio/webm;codecs=opus',  // Bäst: OPUS i WebM-container (ca 10x mindre än WAV)
    'audio/ogg;codecs=opus',   // Alternativ: OPUS i OGG
    'audio/webm',              // Fallback: WebM
    ''                         // Sista utväg: låt webbläsaren välja
  ];

  for (const mimeType of mimeTypes) {
    if (mimeType === '' || MediaRecorder.isTypeSupported(mimeType)) {
      console.log('🎤 Vald mimeType för inspelning:', mimeType || 'browser default');
      return mimeType;
    }
  }
  return '';
};

// Hämta filändelse baserat på mimeType
const getFileExtension = (mimeType) => {
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'm4a';
  return 'webm'; // Default
};

function JournalSection({
  index,
  section,
  handleSectionChange,
  handleDeleteSection,
  handleCopyContent,
  showDeleteButton = false,
  showCopyButton = false,
  readOnly = false,
  sessionId = null, // Session ID for prioritization
}) {
  const safeTitle = (section.title || '').replace(/\n+$/, '');
  const [localContent, setLocalContent] = useState(
    (section.content || '').replace(/\n+$/, '')
  );
  const textareaRef = useRef(null);

  // Fraslexikon: `;` + triggerord infogar en personlig fras
  const {
    showDropdown: showPhraseDropdown,
    dropdownPosition,
    searchQuery: phraseSearchQuery,
    filteredPhrases,
    selectedIndex: phraseSelectedIndex,
    handleKeyDown: handlePhraseKeyDown,
    handleInput: handlePhraseInput,
    selectPhrase,
    closeDropdown: closePhraseDropdown,
    loading: phrasesLoading
  } = useQuickPhrases(textareaRef, (newValue) => {
    setLocalContent(newValue);
    handleSectionChange(index, 'content', newValue);
  });

  // Inspelningsstate
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const mimeTypeRef = useRef(''); // Spara vald mimeType

  // Rå text (partiell) för GPT + position
  const [partialText, setPartialText] = useState('');
  const partialTextRef = useRef('');
  const [cursorPos, setCursorPos] = useState(0);
  const insertionPosRef = useRef(0);
  
  // Dedikerad array för audio chunks
  const chunksRef = useRef([]);
  const pendingChunksRef = useRef([]); // Chunks som väntar på att skickas

  // Timer
  const [startTime, setStartTime] = useState(null);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const chunkTimerRef = useRef(null);
  const lastChunkTimeRef = useRef(0); // Använd tid istället för sekunder
  const chunkIndexRef = useRef(0); // Track chunk index for prioritization
  const isProcessingRef = useRef(false); // Förhindra överlappande API-anrop
  const dictationSessionIdRef = useRef(''); // Unikt session_id per diktering (skapas vid start)

  // Spara position när partialText uppdateras under inspelning
  useEffect(() => {
    if (isRecording && partialText) {
      insertionPosRef.current = cursorPos;
    }
  }, [partialText, isRecording, cursorPos]);

  // Starta / stoppa inspelning
  const handleToggleRecording = async () => {
    if (!isRecording) {
      await startRecording();
    } else {
      await stopRecording();
    }
  };

  const startRecording = async () => {
    try {
      // Rensa alla gamla refs innan start
      chunksRef.current = [];
      pendingChunksRef.current = [];
      partialTextRef.current = '';
      setPartialText('');
      chunkIndexRef.current = 0;
      lastChunkTimeRef.current = 0;
      isProcessingRef.current = false;
      // Skapa unikt session_id för denna diktering (inte parent session som kan vara "completed")
      dictationSessionIdRef.current = `dictation_${index}_${Date.now()}`;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      // Välj bästa mimeType (OPUS prioriteras)
      const selectedMimeType = getSupportedMimeType();
      mimeTypeRef.current = selectedMimeType;

      const recorderOptions = selectedMimeType ? { mimeType: selectedMimeType } : {};
      const recorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = recorder;

      // Kontinuerlig chunk-insamling med timeslice
      // Chunks kommer automatiskt var 200ms
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Starta med timeslice för kontinuerlig data-insamling
      // 200ms timeslice ger jämn dataström utan stop/start overhead
      recorder.start(200);
      setIsRecording(true);
      setStartTime(Date.now());
      setTotalElapsed(0);
      setCurrentSeconds(0);

      // Sätt insertion position till aktuell cursor position
      if (textareaRef.current) {
        insertionPosRef.current = textareaRef.current.selectionStart;
        textareaRef.current.focus();
      }

      console.log('🎤 Inspelning startad i JournalSection', index, 'på position', insertionPosRef.current);
    } catch (err) {
      console.error('Kunde inte starta inspelning:', err);
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current) return;

    setIsRecording(false);
    clearInterval(chunkTimerRef.current);

    // Stoppa recorder och vänta på alla chunks
    const finalPromise = new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;

      recorder.onstop = async () => {
        // Skapa final blob från alla insamlade chunks
        if (chunksRef.current.length > 0) {
          const blobType = mimeTypeRef.current || 'audio/webm';
          const finalBlob = new Blob(chunksRef.current, { type: blobType });
          chunksRef.current = [];

          // Skicka final chunk med högre prioritet
          if (finalBlob.size > 0) {
            console.log('Skickar final chunk, storlek:', finalBlob.size);
            await transcribeChunk(finalBlob, true);
          }
        }

        // Stoppa audio stream
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(t => t.stop());
          audioStreamRef.current = null;
        }

        resolve();
      };

      recorder.stop();
      mediaRecorderRef.current = null;
    });

    await finalPromise;

    // Vänta kort för att säkerställa att sista transkriptionen kommer fram
    await new Promise(resolve => setTimeout(resolve, 300));

    // Gör GPT-cleanup på all insamlad text
    const finalText = partialTextRef.current.trim();
    if (finalText) {
      console.log('Stop -> GPT-cleanup för text:', finalText);
      await cleanupPartialText(finalText);
    } else {
      console.log('Ingen text att rensa upp');
    }

    // Rensa alla refs
    partialTextRef.current = '';
    setPartialText('');
    chunksRef.current = [];
    pendingChunksRef.current = [];
    chunkIndexRef.current = 0;
    isProcessingRef.current = false;

    console.log('🎤 Inspelning stoppad i JournalSection', index);
  };

  // Near-realtime chunk-timer - snabbare intervall för live streaming
  useEffect(() => {
    if (isRecording && startTime) {
      // Använd 100ms interval för responsiv UI och exakt timing
      chunkTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const totalMs = totalElapsed + elapsed;
        const totalSec = Math.floor(totalMs / 1000);
        setCurrentSeconds(totalSec);

        // Skicka chunks baserat på tid (ms) för mer exakt kontroll
        const timeSinceStart = totalMs / 1000;
        const timeSinceLastChunk = timeSinceStart - lastChunkTimeRef.current;

        // Starta efter CHUNK_START_AFTER sekunder, sedan varje CHUNK_INTERVAL
        if (
          timeSinceStart >= CHUNK_START_AFTER &&
          timeSinceLastChunk >= CHUNK_INTERVAL &&
          !isProcessingRef.current
        ) {
          lastChunkTimeRef.current = timeSinceStart;
          sendAccumulatedChunks();
        }
      }, 100); // 100ms för responsiv feedback
    }
    return () => {
      if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, startTime, totalElapsed]);

  // Skicka ackumulerade chunks utan att stoppa recorder
  // VIKTIGT: WebM/Opus kräver alla chunks från början för att vara giltigt
  // Vi skickar hela inspelningen varje gång och ersätter partialText helt
  const sendAccumulatedChunks = async () => {
    if (chunksRef.current.length === 0 || isProcessingRef.current) return;

    // Markera att vi processar för att undvika överlappande anrop
    isProcessingRef.current = true;

    // VIKTIGT: Skicka ALLA chunks från början (inte bara nya) för giltig WebM
    const allChunks = [...chunksRef.current];
    // Rensa INTE chunksRef - vi behöver alla chunks för nästa request också

    const blobType = mimeTypeRef.current || 'audio/webm';
    const blob = new Blob(allChunks, { type: blobType });

    if (blob.size > 0) {
      console.log('Skickar streaming chunk, storlek:', blob.size, 'chunks:', allChunks.length);
      try {
        await transcribeChunk(blob, false);
      } catch (error) {
        console.error('Fel vid transcribeChunk:', error);
      }
    }

    isProcessingRef.current = false;
  };

  // transcribeChunk
  const transcribeChunk = async (blob, isFinalChunk = false) => {
    if (!blob) return;
    try {
      const formData = new FormData();
      // Använd korrekt filändelse baserat på mimeType
      const fileExt = getFileExtension(mimeTypeRef.current);
      formData.append('file', blob, `chunk.${fileExt}`);
      
      // Använd dikteringens egna session_id (skapas vid inspelningsstart)
      formData.append('session_id', dictationSessionIdRef.current);
      formData.append('chunk_index', chunkIndexRef.current.toString());

      // Calculate total duration in seconds
      const totalDuration = Math.floor((totalElapsed + (startTime ? Date.now() - startTime : 0)) / 1000);
      formData.append('total_duration', totalDuration.toString());

      // Live-diktering ska ALLTID ha HIGH priority (action=finish triggar detta på Whisper-servern)
      // Detta säkerställer att chunks processas direkt istället för att köas
      formData.append('action', 'finish');
      formData.append('is_final', isFinalChunk.toString());
      
      // Use new streaming endpoint with session tracking
      const resp = await apiClient.post('/api/dictation/streaming_chunk', formData);
      
      // Handle different response types
      console.log('📝 API Response:', resp.data.status, 'transcription:', resp.data.transcription?.substring(0, 50));

      if (resp.data.status === 'queued') {
        console.log(`⏳ Chunk ${chunkIndexRef.current} queued with priority:`, resp.data.priority);
      } else if (resp.data.status === 'completed' || resp.data.transcription) {
        const partial = resp.data.transcription || '';
        const trimmed = partial.trim();

        console.log('✅ Got transcription:', trimmed);

        if (trimmed) {
          // ERSÄTT hela partialText (vi transkriberar hela inspelningen varje gång)
          partialTextRef.current = trimmed;
          setPartialText(trimmed);
          console.log('📝 partialText ersatt med:', trimmed);

          // Log medical corrections if applied
          if (resp.data.medical_correction_applied && resp.data.corrections?.length > 0) {
            console.log('Medical corrections applied:', resp.data.corrections);
          }
        }
      }
      
      // Increment chunk index for next chunk
      chunkIndexRef.current += 1;
      
    } catch (err) {
      console.error('Fel vid transkribering av chunk:', err);
      // On error, still increment chunk index to maintain sequence
      chunkIndexRef.current += 1;
    }
  };

  // GPT-cleanup (infoga vid sparad cursor-position)
  const cleanupPartialText = async (rawText) => {
    if (!rawText.trim()) return;
    try {
      console.log('Skickar raw text till GPT-cleanup:', rawText);
      const resp = await apiClient.post('/api/dictation/cleanup', { 
        raw_text: rawText,
        preserve_medical_terms: true,
        language: 'swedish',
        source: 'journal_section'
      });
      
      const cleaned = resp.data?.success ? resp.data.cleaned_text : rawText;
      console.log('GPT-cleanup =>', cleaned);

      const base = localContent;
      const insertAt = insertionPosRef.current;
      const updated = base.slice(0, insertAt) + cleaned + base.slice(insertAt);

      setLocalContent(updated);
      handleSectionChange(index, 'content', updated);
      scrollToBottom();

      // Sätt cursor efter den infogade texten
      const newCursorPos = insertAt + cleaned.length;
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 100);

      // Rensa partial state
      partialTextRef.current = '';
      setPartialText('');
      insertionPosRef.current = 0;
    } catch (err) {
      console.error('Fel vid GPT-cleanup:', err);
      // Fallback: infoga raw text om cleanup misslyckas
      const base = localContent;
      const insertAt = insertionPosRef.current;
      const updated = base.slice(0, insertAt) + rawText + base.slice(insertAt);
      setLocalContent(updated);
      handleSectionChange(index, 'content', updated);
      
      partialTextRef.current = '';
      setPartialText('');
      insertionPosRef.current = 0;
    }
  };

  // scrollToBottom
  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      }
    });
  };

  // Synka in text som kommer utifrån (generering, sessionbyte) utan avslutande tomrader.
  // Skriv INTE över om skillnaden bara är avslutande radbrytningar — då är det
  // användarens egen Enter i slutet av sektionen, som annars försvinner och
  // markören hoppar tillbaka.
  useEffect(() => {
    const incoming = (section.content || '').replace(/\n+$/, '');
    setLocalContent(prev => (prev.replace(/\n+$/, '') === incoming ? prev : incoming));
  }, [section.content]);

  useEffect(() => {
    autoResize();
  }, [localContent]);

  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Hantera manuell ändring
  const handleChange = (e) => {
    const newValue = e.target.value;

    // Om vi har partial text, hantera det separat
    if (partialText) {
      // Under inspelning, updatera bara localContent, inte partial text
      const beforePartial = localContent.slice(0, cursorPos);
      const afterPartial = localContent.slice(cursorPos);
      const expectedDisplayValue = beforePartial + partialText + afterPartial;

      // Om användaren ändrar i området med partial text, avbryt partial
      if (newValue !== expectedDisplayValue) {
        console.log('User edited during recording, clearing partial text');
        partialTextRef.current = '';
        setPartialText('');
        setLocalContent(newValue);
        handleSectionChange(index, 'content', newValue);
      }
    } else {
      // Normal redigering
      setLocalContent(newValue);
      handleSectionChange(index, 'content', newValue);
    }

    // Handle quick phrase trigger (;)
    handlePhraseInput(e);

    autoResize();
  };

  // Handle keyboard events for quick phrases
  const handleTextareaKeyDown = (e) => {
    // Let quick phrases hook handle navigation keys first
    // (inte under inspelning — då innehåller textrutan ofärdig dikteringstext)
    if (showPhraseDropdown && isRecording) {
      closePhraseDropdown();
      return;
    }
    if (showPhraseDropdown) {
      const handled = handlePhraseKeyDown(e);
      if (handled) return;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      autoResize();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Cleanup vid unmount
  useEffect(() => {
    return () => {
      // Säkerställ att allt stängs av ordentligt
      if (mediaRecorderRef.current) {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          // Ignorera fel om redan stoppad
        }
        mediaRecorderRef.current = null;
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          try {
            track.stop();
          } catch (e) {
            // Ignorera fel om redan stoppad
          }
        });
        audioStreamRef.current = null;
      }

      // Rensa alla chunks och text
      chunksRef.current = [];
      pendingChunksRef.current = [];
      partialTextRef.current = '';
      isProcessingRef.current = false;

      // Rensa timers
      if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
    };
  }, []);

  // Beräkna vad som visas i textarea
  const displayValue = partialText && isRecording
   ? localContent.slice(0, insertionPosRef.current) + partialText + localContent.slice(insertionPosRef.current)
   : localContent;

  // Render
  return (
    <div className="journal-section">
      <h4>{safeTitle}</h4>
      {isRecording && (
        <div className="recording-time">
          Inspelad tid: {currentSeconds} s
        </div>
      )}
      <div className="journal-section-row">
        <div className="textarea-wrapper">
          {showCopyButton && (
            <button
              className="cta"
              onClick={handleCopyContent}
              title="Kopiera innehåll"
            >
              <span>Kopiera</span>
              <svg width="10px" height="10px" viewBox="0 0 13 10">
                <path d="M1,5 L11,5"></path>
                <polyline points="8 1 12 5 8 9"></polyline>
              </svg>
            </button>
          )}
          <textarea
            ref={textareaRef}
            className="journal-section-content"
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleTextareaKeyDown}
            onSelect={(e) => {
              if (!isRecording) {
                setCursorPos(e.target.selectionStart);
              }
            }}
            onKeyUp={(e) => {
              if (!isRecording) {
                setCursorPos(e.target.selectionStart);
              }
            }}
            onClick={(e) => {
              if (!isRecording) {
                setCursorPos(e.target.selectionStart);
                insertionPosRef.current = e.target.selectionStart;
              }
            }}
            readOnly={readOnly || (isRecording && partialText)}
            rows={1}
          />

          {/* Quick Phrase Dropdown */}
          <QuickPhraseDropdown
            show={showPhraseDropdown && !isRecording}
            position={dropdownPosition}
            phrases={filteredPhrases}
            selectedIndex={phraseSelectedIndex}
            searchQuery={phraseSearchQuery}
            onSelect={selectPhrase}
            onClose={closePhraseDropdown}
            loading={phrasesLoading}
          />

          {!readOnly && (
            <>
              <input
                type="checkbox"
                id={`checkbox-${index}`}
                checked={isRecording}
                className="mic-toggle"
                onChange={handleToggleRecording}
              />
              <label className="switch" htmlFor={`checkbox-${index}`}>
                <div className="mic-on">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-mic-fill" viewBox="0 0 16 16">
                                  <path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0V3z" />
                                  <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z" />
                  </svg>
                </div>
                <div className="mic-off">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-mic-mute-fill" viewBox="0 0 16 16">
                                  <path d="M13 8c0 .564-.094 1.107-.266 1.613l-.814-.814A4.02 4.02 0 0 0 12 8V7a.5.5 0 0 1 1 0v1zm-5 4c.818 0 1.578-.245 2.212-.667l.718.719a4.973 4.973 0 0 1-2.43.923V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 1 0v1a4 4 0 0 0 4 4zm3-9v4.879L5.158 2.037A3.001 3.001 0 0 1 11 3z" />
                                  <path d="M9.486 10.607 5 6.12V8a3 3 0 0 0 4.486 2.607zm-7.84-9.253 12 12 .708-.708-12-12-.708.708z" />
                  </svg>
                </div>
              </label>
            </>
          )}

        </div>

        {showDeleteButton && (
          <button
            className="btn"
            onClick={() => handleDeleteSection(index)}
            title="Ta bort rubrik"
          >
            <p className="paragraph">delete</p>
            <span className="icon-wrapper">
              <svg
                              className="icon"
                              width="30px"
                              height="30px"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M6 7V18C6 19.1046 6.89543 20 8 20H16C17.1046 20 18 19.1046 18 18V7M6 7H5M6 7H8M18 7H19M18 7H16M10 11V16M14 11V16M8 7V5C8 3.89543 8.89543 3 10 3H14C15.1046 3 16 3.89543 16 5V7M8 7H16"
                                stroke="#000000"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
              </svg>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

export default JournalSection;
